// One partner rule as a card (Phase 4b plan §2.1, §2.3). The owner's card has the switch, the
// "shoppers we can't locate" radios and Change; another retailer's card is read-only with a lock:
// title, sentence and start date only (the sentence already says where).
import React from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import { LockOutlined } from '@mui/icons-material';
import { footer, ruleSentence, ruleTitle, townEdge, whereLine, whoLine } from './copy';

const Row = ({ label, children, action }) => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      gap: 2,
      py: 1.5,
      borderTop: '1px solid rgba(0,0,0,0.08)',
    }}
  >
    <Box sx={{ minWidth: 0 }}>
      {label && (
        <Typography variant="overline" component="div" color="text.secondary" sx={{ lineHeight: 1.6 }}>
          {label}
        </Typography>
      )}
      {children}
    </Box>
    {action}
  </Box>
);

export default function RuleCard({ card, own, canEdit, busy, onToggle, onUnknown, onChange }) {
  const edge = own && card.where.type === 'radius' ? townEdge(card.where.towns) : null;
  const editable = own && canEdit && card.on;
  const change = editable ? (
    <Button size="small" onClick={() => onChange(card)} sx={{ minHeight: 44 }}>
      Change
    </Button>
  ) : null;

  return (
    <Card>
      <CardContent sx={{ px: { xs: 2, sm: 3 }, '&:last-child': { pb: 2 } }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
          <Typography variant="h6" component="h3" sx={{ fontSize: 18, lineHeight: 1.35 }}>
            {ruleTitle(card, own)}
          </Typography>
          {own ? (
            <FormControlLabel
              labelPlacement="start"
              label={card.on ? 'On' : 'Off'}
              sx={{ mr: 0, flex: 'none' }}
              control={
                <Switch
                  checked={card.on}
                  disabled={!canEdit || busy}
                  onChange={() => onToggle(card)}
                  inputProps={{ 'aria-label': 'Rule on or off' }}
                />
              }
            />
          ) : (
            <LockOutlined
              fontSize="small"
              color="action"
              titleAccess="You cannot change this rule"
              sx={{ flex: 'none', mt: 0.5 }}
            />
          )}
        </Stack>

        <Typography sx={{ mt: 1, mb: 0.5 }} color={card.on ? 'text.primary' : 'text.secondary'}>
          {ruleSentence(card, own)}
        </Typography>

        {own && (
          <>
            <Row label="Who" action={change}>
              <Typography>{whoLine(card)}</Typography>
            </Row>
            <Row label="Where" action={change}>
              <Typography>{whereLine(card)}</Typography>
              {edge && (
                <Typography variant="body2" color="text.secondary">
                  {edge}
                </Typography>
              )}
            </Row>
          </>
        )}

        {own && card.scoped && (
          <Row label="Shoppers we can't locate">
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              Phones and VPNs often hide where a shopper is.
            </Typography>
            <RadioGroup
              value={card.unknown_excluded ? 'exclude' : 'show'}
              onChange={(e) => onUnknown(card, e.target.value === 'exclude')}
              aria-label="Shoppers we can't locate"
            >
              <FormControlLabel value="show" disabled={!editable || busy} control={<Radio />} label="Don't exclude them" />
              <FormControlLabel value="exclude" disabled={!editable || busy} control={<Radio />} label="Exclude them too" />
            </RadioGroup>
          </Row>
        )}

        {own && (
          <Row>
            <Stack direction="row" spacing={1} alignItems="flex-start">
              <LockOutlined fontSize="small" color="action" titleAccess="Fixed" sx={{ mt: 0.25 }} />
              <Typography variant="body2" color="text.secondary">
                The rule works both ways. Partners cannot switch it off.
              </Typography>
            </Stack>
          </Row>
        )}

        <Typography variant="caption" component="div" color="text.secondary" sx={{ pt: 1 }}>
          {footer(card, own)}
        </Typography>
      </CardContent>
    </Card>
  );
}
